type SupportedLanguage = 'bg' | 'en' | 'nl' | 'tr' | 'pl';

interface CategoryCanonicalLabel {
  bg: string;
  en: string;
  nl: string;
  tr: string;
  pl: string;
}

const CATEGORY_LABELS: Record<string, CategoryCanonicalLabel> = {
  construction: { bg: 'Строителство', en: 'Construction', nl: 'Bouw', tr: 'İnşaat', pl: 'Budownictwo' },
  electrical: { bg: 'Електротехника', en: 'Electrical', nl: 'Elektriciteit', tr: 'Elektrik', pl: 'Elektryka' },
  plumbing: { bg: 'Водопровод', en: 'Plumbing', nl: 'Loodgieterij', tr: 'Tesisat', pl: 'Hydraulika' },
  cleaning: { bg: 'Почистване', en: 'Cleaning', nl: 'Schoonmaak', tr: 'Temizlik', pl: 'Sprzątanie' },
  gardening: { bg: 'Градинарство', en: 'Gardening', nl: 'Tuinieren', tr: 'Bahçıvanlık', pl: 'Ogrodnictwo' },
  transportation: { bg: 'Транспорт', en: 'Transportation', nl: 'Transport', tr: 'Ulaşım', pl: 'Transport' },
  computer_services: { bg: 'Компютърни услуги', en: 'Computer Services', nl: 'Computerdiensten', tr: 'Bilgisayar Hizmetleri', pl: 'Usługi komputerowe' },
  design: { bg: 'Дизайн', en: 'Design', nl: 'Ontwerp', tr: 'Tasarım', pl: 'Projektowanie' },
  photography: { bg: 'Фотография', en: 'Photography', nl: 'Fotografie', tr: 'Fotoğrafçılık', pl: 'Fotografia' },
  education: { bg: 'Обучение', en: 'Education', nl: 'Onderwijs', tr: 'Eğitim', pl: 'Edukacja' },
  it_services: { bg: 'Информационни технологии', en: 'Information Technology', nl: 'Informatietechnologie', tr: 'Bilgi Teknolojileri', pl: 'Informatyka' },
  business_services: { bg: 'Бизнес услуги', en: 'Business Services', nl: 'Zakelijke diensten', tr: 'İş Hizmetleri', pl: 'Usługi biznesowe' },
  health_wellbeing: { bg: 'Здраве и благосъстояние', en: 'Health & Well-being', nl: 'Gezondheid en welzijn', tr: 'Sağlık ve Esenlik', pl: 'Zdrowie i samopoczucie' },
  home_garden: { bg: 'Дом и градина', en: 'Home & Garden', nl: 'Huis en tuin', tr: 'Ev ve Bahçe', pl: 'Dom i ogród' },
  transport_logistics: { bg: 'Транспорт и логистика', en: 'Transport & Logistics', nl: 'Transport en logistiek', tr: 'Ulaşım ve Lojistik', pl: 'Transport i logistyka' },
  legal_services: { bg: 'Правни услуги', en: 'Legal Services', nl: 'Juridische diensten', tr: 'Hukuki Hizmetler', pl: 'Usługi prawne' },
  arts_entertainment: { bg: 'Изкуство и развлечения', en: 'Arts & Entertainment', nl: 'Kunst en entertainment', tr: 'Sanat ve Eğlence', pl: 'Sztuka i rozrywka' },
};

const CATEGORY_ALIASES: Record<string, keyof typeof CATEGORY_LABELS> = {
  construction: 'construction',
  строителство: 'construction',
  bouw: 'construction',
  electrical: 'electrical',
  електротехника: 'electrical',
  elektriciteit: 'electrical',
  plumbing: 'plumbing',
  водопровод: 'plumbing',
  loodgieterij: 'plumbing',
  cleaning: 'cleaning',
  почистване: 'cleaning',
  schoonmaak: 'cleaning',
  gardening: 'gardening',
  градинарство: 'gardening',
  tuinieren: 'gardening',
  transportation: 'transportation',
  транспорт: 'transportation',
  transport: 'transportation',
  'computer services': 'computer_services',
  'компютърни услуги': 'computer_services',
  computerdiensten: 'computer_services',
  design: 'design',
  'design services': 'design',
  дизайн: 'design',
  ontwerp: 'design',
  photography: 'photography',
  фотография: 'photography',
  fotografie: 'photography',
  education: 'education',
  обучение: 'education',
  onderwijs: 'education',
  'information technology': 'it_services',
  'информационни технологии': 'it_services',
  informatietechnologie: 'it_services',
  'business services': 'business_services',
  'бизнес услуги': 'business_services',
  'zakelijke diensten': 'business_services',
  'health & well-being': 'health_wellbeing',
  'health and well-being': 'health_wellbeing',
  'здраве и благосъстояние': 'health_wellbeing',
  'gezondheid en welzijn': 'health_wellbeing',
  'home & garden': 'home_garden',
  home: 'home_garden',
  'дом и градина': 'home_garden',
  'huis en tuin': 'home_garden',
  'transport & logistics': 'transport_logistics',
  'transport and logistics': 'transport_logistics',
  'транспорт и логистика': 'transport_logistics',
  'transport en logistiek': 'transport_logistics',
  'legal services': 'legal_services',
  'правни услуги': 'legal_services',
  'juridische diensten': 'legal_services',
  'arts & entertainment': 'arts_entertainment',
  'arts and entertainment': 'arts_entertainment',
  'изкуство и развлечения': 'arts_entertainment',
  'kunst en entertainment': 'arts_entertainment',
};

const normalizeLabel = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

export const resolveCategoryCanonicalKey = (categoryName: string): string | null => {
  if (!categoryName) return null;
  const aliasKey = normalizeLabel(categoryName);
  const keyFromNormalized = aliasKey.replace(/\s+/g, '_');
  if (CATEGORY_LABELS[keyFromNormalized]) {
    return keyFromNormalized;
  }
  if (CATEGORY_ALIASES[aliasKey]) {
    return CATEGORY_ALIASES[aliasKey];
  }

  for (const [alias, canonical] of Object.entries(CATEGORY_ALIASES)) {
    if (aliasKey.includes(alias)) {
      return canonical;
    }
  }

  return null;
};

// Функция за превод на категория
export const translateCategory = (categoryName: string, language: string): string => {
  if (!categoryName) return '';

  const code = language.split('-')[0];
  const normalizedLanguage: SupportedLanguage =
    code === 'bg' || code === 'en' || code === 'nl' || code === 'tr' || code === 'pl'
      ? code
      : 'en';

  const canonicalKey = resolveCategoryCanonicalKey(categoryName);
  if (!canonicalKey) {
    return categoryName;
  }

  return CATEGORY_LABELS[canonicalKey][normalizedLanguage];
};